import type { Contact } from '@/api/queries/conversations';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactList } from './ContactList';

interface CreateNewConversationProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateConversation: (title: string, selectedContact: Contact) => Promise<{ success: boolean }>;
  isLoading?: boolean;
}

export function CreateNewConversation({
  isOpen,
  onClose,
  onCreateConversation,
  isLoading = false,
}: CreateNewConversationProps) {
  const { t } = useTranslation();
  const { t: tConversations } = useTranslation('conversations');
  const [conversationTitle, setConversationTitle] = useState('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const handleContactSelect = (contact: Contact) => {
    setSelectedContact(contact);
  };

  const resetForm = () => {
    setConversationTitle('');
    setSelectedContact(null);
  };

  const handleCreateConversation = async () => {
    if (conversationTitle.trim() && selectedContact) {
      const { success } = await onCreateConversation(conversationTitle.trim(), selectedContact);
      if (success) {
        resetForm();
      }
    }
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const canCreate = conversationTitle.trim() && selectedContact && !isLoading;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl flex flex-col">
        {/* Header */}
        <DialogHeader className="pb-3">
          <DialogTitle>{tConversations('newConversation.modalTitle')}</DialogTitle>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 flex flex-col space-y-3">
          {/* Conversation Title */}
          <div className="space-y-2">
            <label htmlFor="conversation-title" className="text-sm font-medium text-gray-700">
              {tConversations('newConversation.newConversationTitle')}
            </label>
            <Input
              id="conversation-title"
              type="text"
              placeholder={tConversations('newConversation.titlePlaceholder')}
              value={conversationTitle}
              onChange={e => setConversationTitle(e.target.value)}
              className="w-full"
              disabled={isLoading}
              required
              data-testid="conversation-title-input"
            />
          </div>

          {/* Selected Contact Display */}
          {selectedContact && (
            <div
              className="p-3 bg-gray-dark-1 text-white rounded-lg"
              data-testid={`selected-contact-${selectedContact.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {tConversations('newConversation.selectedContact')}
                  </p>
                  <p className="text-sm ">{selectedContact.name}</p>
                  {selectedContact.email && <p className="text-xs ">{selectedContact.email}</p>}
                </div>
                <Check className="w-5 h-5 text-white" />
              </div>
            </div>
          )}
          {/* Contact Selection */}
          <div className="flex-1 flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {tConversations('newConversation.selectContact')}
            </label>
            <div className="flex-1 border border-gray-200 rounded-lg p-2">
              <ContactList onContactClick={handleContactSelect} disabled={isLoading} />
            </div>
          </div>

          {/* Create Button */}
          <Button
            onClick={handleCreateConversation}
            disabled={!canCreate}
            className="w-full"
            data-testid="create-conversation-button"
          >
            {isLoading ? t('loading') : tConversations('newConversation.create')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
